// Receipt drawer state + open helper. Each call site (SessionsTimelineCard,
// SessionCard, SessionDetailPage) instantiates its own copy — the drawer is
// modal so there's at most one open at a time per call site, and reusing
// state across them would require a portal-level context that isn't worth
// the indirection right now.
//
// Why a hook at all: the open flow is "fetch the PDF blob → trigger browser
// download → also keep the blob URL alive and surface it in <ReceiptDrawer>",
// which is enough plumbing to deserve its own reusable wrapper. On close we
// revoke the blob URL so the browser can free memory.

import { useCallback, useEffect, useRef, useState } from 'react'
import { downloadReceiptPdf } from '../services/sessionsService'

const EMPTY = Object.freeze({
  open: false,
  loading: false,
  filename: null,
  blobUrl: null,
  mimeType: null,
  title: null,
  subtitle: null,
  eventId: null,
  fallbackText: null,
  error: null,
})

export function useReceiptDrawer() {
  const [state, setState] = useState(EMPTY)
  // Track the last blob URL we created so the next open can revoke it
  // before allocating a new one — prevents a leak when the user opens
  // multiple receipts in succession without closing.
  const lastBlobUrlRef = useRef(null)

  const close = useCallback(() => {
    if (lastBlobUrlRef.current) {
      try { URL.revokeObjectURL(lastBlobUrlRef.current) } catch (e) { /* ignore */ }
      lastBlobUrlRef.current = null
    }
    setState(EMPTY)
  }, [])

  // Free the blob URL on unmount too — a SessionsTimelineCard re-renders /
  // unmounts when data refreshes; without this the URL stays allocated.
  useEffect(() => () => {
    if (lastBlobUrlRef.current) {
      try { URL.revokeObjectURL(lastBlobUrlRef.current) } catch (e) { /* ignore */ }
      lastBlobUrlRef.current = null
    }
  }, [])

  /**
   * Fetch the receipt PDF for {profile, eventId} and open the drawer.
   *
   * @param {object} profile  active PatientProfile
   * @param {object} session  SessionSummary-shaped object — used for title /
   *                          subtitle / fallback text in the mock path
   * @param {object} [opts]   { download?: boolean, lang?: 'en'|'hi'|… }
   */
  const open = useCallback(async (profile, session, opts = {}) => {
    if (!profile || !session?.eventId) return
    if (lastBlobUrlRef.current) {
      try { URL.revokeObjectURL(lastBlobUrlRef.current) } catch (e) { /* ignore */ }
      lastBlobUrlRef.current = null
    }
    setState({
      ...EMPTY,
      open: true,
      loading: true,
      eventId: session.eventId,
      title: 'Receipt',
      subtitle: subtitleFor(session),
    })
    try {
      const result = await downloadReceiptPdf(profile, session.eventId, {
        download: opts.download !== false,
        lang: opts.lang,
      })
      lastBlobUrlRef.current = result.blobUrl
      // Mock-mode blob is a text/plain note — surface its body as a
      // fallback so users see something meaningful instead of an empty
      // iframe with the wrong content type.
      let fallbackText = null
      if (result.blob && result.mimeType && !result.mimeType.includes('pdf')) {
        try { fallbackText = await result.blob.text() } catch (e) { /* ignore */ }
      }
      setState({
        open: true,
        loading: false,
        filename: result.filename,
        blobUrl: result.blobUrl,
        mimeType: result.mimeType,
        title: 'Receipt',
        subtitle: subtitleFor(session),
        eventId: session.eventId,
        fallbackText,
        error: null,
      })
    } catch (e) {
      setState({
        ...EMPTY,
        open: true,
        loading: false,
        title: 'Receipt',
        subtitle: subtitleFor(session),
        eventId: session.eventId,
        error: e?.message || 'Could not load receipt. Try again in a minute.',
      })
    }
  }, [])

  return { state, open, close }
}

function subtitleFor(session) {
  if (!session) return null
  const parts = []
  if (session.title) parts.push(session.title)
  if (session.scheduledAt) {
    try {
      // Server emits "yyyy-MM-dd HH:mm:ss" UTC; render in local format.
      const d = new Date(String(session.scheduledAt).replace(' ', 'T') + 'Z')
      if (!Number.isNaN(d.getTime())) {
        parts.push(
          d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }),
        )
      }
    } catch (e) {
      /* ignore — fall back to raw */
    }
  }
  return parts.join(' · ') || null
}
